import { html, css } from 'lit'
import HHQuizElement from '../element.js'
import { handleize } from '../../lib/utils'
import './progress-bar.js'
import { repeat } from 'lit/directives/repeat.js'

/**
 * Quiz main linear navigation
 * @extends
 */

class HHQuizNavigation extends HHQuizElement {
  getCategoryProps(category) {
    /**
     * Category path => Implemented as first question in the category (categories don't have a path)
     */
    const path = `/${category.handle}/${handleize(
      category.questions[0].handle
    )}`

    /**
     * Determines if the router before the current category
     */
    const routerIsBefore =
      this.router.linearNavigationPaths.indexOf(
        this.router.current.sanitizedPath
      ) < this.router.linearNavigationPaths.indexOf(path)

    /**
     * Determines if the router is in the current category
     */
    const routerIsInCategory = this.router.current.sanitizedPath.startsWith(
      `/${category.handle}/`
    )

    /**
     * Linear navigation paths for the current category
     */
    const categoryPaths = this.router.linearNavigationPaths.filter((p) =>
      p.startsWith(`/${category.handle}/`)
    )

    /**
     *  0: router is before category
     *  (0,1]: router is in category
     *  1: router is after category
     *
     *  this extracts a progress based on
     *  - the current router path
     *  - the current category progress
     *  Router progress serves as a maximum value, for when the user navigates back to a previous question
     */
    const progress = routerIsBefore
      ? 0
      : routerIsInCategory
      ? Math.min(
          // Router progress
          (categoryPaths.indexOf(this.router.current.sanitizedPath) + 1) /
            (categoryPaths.length + 1) +
            1 / (categoryPaths.length + 1),

          // Category progress
          category.progress -
            category.progress * (1 / (category.questions.length + 1)) +
            1 / (category.questions.length + 1)
        )
      : 1

    return {
      progress,
      path,
      routerIsInCategory,
    }
  }
  /**
   * Renders the navigation from Intro to last question
   * @returns {TemplateResult}
   */
  render() {
    return html`<nav class="hh-quiz-navigation">
      <div>
        <hh-quiz-router-link class="h3" to="/">
          <hh-quiz-progress-bar .progress="${this.store.customerProgress}" .animate="${this.router.current.sanitizedPath == "/"}"></hh-quiz-progress-bar>
          <span>Get started</span>
        </hh-quiz-router-link>
      </div>
      ${repeat(
        this.store.quizCategories,
        (category) => category.handle,
        (category) => {
          const { progress, path, routerIsInCategory } = this.getCategoryProps(
            category
          )

          return html` <div>
            <hh-quiz-router-link
              class="h3 ${category.isEnabled ? '' : 'is-disabled'}"
              .disabled="${!category.isEnabled}"
              to="${path}"
            >
              <hh-quiz-progress-bar
                .progress="${progress}"
                .animate="${routerIsInCategory}"
              ></hh-quiz-progress-bar>
              <span>${category.name}</span>
            </hh-quiz-router-link>
          </div>`
        }
      )}
    </nav>`
  }

  static styles = [
    HHQuizElement.styles,
    css`
      .h3 {
        text-decoration:none !important;
      }
      .hh-quiz-navigation {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(0, 1fr));
        text-align: center;
        gap: 10px;
        padding: 20px 30px;
      }

      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        .hh-quiz-navigation {
          padding: 20px 20px;
        }
      }

      hh-quiz-router-link {
        display: block;
        color: var(--hh-quiz-color-aged-black);
        text-decoration: none;
      }

      hh-quiz-router-link.is-disabled {
        cursor: not-allowed;
      }

      @media screen and (max-width: ${HHQuizElement.mq.sm}px) {
        hh-quiz-router-link > span {
          display: none;
        }
      }
    `,
  ]
}

customElements.define('hh-quiz-navigation', HHQuizNavigation)
